import { delay } from 'redux-saga';
import { call, put, race, take, takeLatest } from 'redux-saga/effects';
import { oc } from 'ts-optchain';

import { transactionsApi } from '../../../../common/api/transaction';
import { ProductValidity } from '../../../../common/components';
import { AppState } from '../../../../common/models/AppState';
import { createActionCreator, createReducer } from '../../../../common/utils/redux-helpers';
import { selectShopping, TimeSlotType } from '../../ducks';
import { resetCartAction } from '../../ducks/cart';
import i18next from 'i18next';
import { environment } from '../../../../environments/environment';
import { resetTemporaryCardsAction } from '../../../user/ducks';
import { clearEmailStateAction } from '../../ducks/order';
import { Entry, EntryMethod, Transaction, TicketItem } from '../../../../common/models/NewTransaction';
import { sendGA4PurchaseEvent, sendTagManagerPurchase } from '../../../../common/utils/gtm-helpers';

// Action types
export enum ConfirmationPollingActionTypes {
    Start = '@@EshopConfirmation-polling/START',
    Failed = '@@EshopConfirmation-polling/FAILED',
    Error = '@@EshopConfirmation-polling/ERROR',
    Cancel = '@@EshopConfirmation-polling/CANCEL',
    Timeout = '@@EshopConfirmation-polling/TIMEOUT',
    Success = '@@EshopConfirmation-polling/SUCCESS',
}

// Actions
export const startConfirmationPollingAction = createActionCreator(
    ConfirmationPollingActionTypes.Start
);

export const timeoutConfirmationPollingAction = createActionCreator(
    ConfirmationPollingActionTypes.Timeout
);

export const failedConfirmationPollingAction = createActionCreator(
    ConfirmationPollingActionTypes.Failed
);

export const errorConfirmationPollingAction = createActionCreator(
    ConfirmationPollingActionTypes.Error
);

export const successConfirmationPollingAction = createActionCreator(
    ConfirmationPollingActionTypes.Success
);

// Reducers
export interface ConfirmationState {
    transaction: Transaction | null;
    confirmed: boolean;
    error: boolean;
}

const initialState: ConfirmationState = {
    transaction: null,
    confirmed: false,
    error: false,
};

export const confirmation = createReducer(initialState, {
    [ConfirmationPollingActionTypes.Success]: (state, payload) => ({
        ...state,
        transaction: payload,
        confirmed: true,
    }),
    [ConfirmationPollingActionTypes.Error]: (state) => ({
        ...state,
        error: true,
        confirmed: false,
    }),
});

// Selectors
export function selectConfirmation(state: AppState) {
    return selectShopping(state).confirmation;
}

export function selectConfirmedTransaction(state: AppState) {
    return selectConfirmation(state).transaction;
}

export function selectConfirmationError(state: AppState) {
    return selectConfirmation(state).error;
}

export function selectConfirmationStatus(state: AppState) {
    return selectConfirmation(state).confirmed;
}

export function selectTicketEntries(state: AppState): TicketItem[] {
    const transaction = selectConfirmedTransaction(state);

    const getName = (entry: Entry) => {
        const name = {
            name: oc(entry).product.name[i18next.language](''),
            description: oc(entry).product.description[i18next.language](''),
            serial: oc(entry).ticketSystem.output.NSERIALNO(),
            pos: oc(entry).ticketSystem.output.NPOSNO()
        };

        const ticketSerial = name.serial && name.pos ? `[${name.pos}-${name.serial}]` : '';

        return name.description
            ? `${name.name} ${name.description} ${ticketSerial}`
            : `${name.name} ${ticketSerial}`;
    };

    if (transaction) {
        return transaction.entries
            .filter(entry => entry.type !== 'transaction_voucher_product')
            .map(entry => {
                let time = '';

                if (oc(entry).meta.timeSlotType() === TimeSlotType.range) {
                    if (environment.config.showTimeSlotRange && oc(entry).meta.timeSlot.to()) {
                        time = `${oc(entry).meta.timeSlot.from()} - ${oc(entry).meta.timeSlot.to()}`;
                    } else {
                        time = oc(entry).meta.timeSlot.from('');
                    }
                }

                const validity = ProductValidity.format(
                    oc(entry).meta.validFrom(''),
                    oc(entry).product.meta.tokenValidityAmount(1),
                    oc(entry).product.meta.tokenValidityUnit() || 'days',
                    oc(entry).product.meta.seasonal(),
                    time,
                );

                return {
                    uuid: oc(entry).uuid(),
                    name: getName(entry),
                    variant: oc(entry).product.variant[i18next.language](''),
                    validity,
                    exhangeType: entry.token ? (entry.token.meta.subType || 'adult') : null,
                    token: oc(entry).token(),
                    userName: oc(entry).userData() ?
                        `${oc(entry).userData.firstName('')} ${oc(entry).userData.lastName('')}` :
                        undefined,
                    method: oc(entry).meta.method(),
                    isGift: !!oc(entry).meta.giftTemplate(),
                    ...getCodeTicketConfig(entry),
                    smallTicketInfo: [
                        // oc(entry).product.meta.smallTicketInfo.product[i18next.language](),
                        // oc(entry).product.meta.smallTicketInfo.variant[i18next.language](),
                    ].filter(i => i),
                    walletData: oc(entry).walletData(),
                } as TicketItem;
            });
    } else {
        return [];
    }
}

export function selectTransactionValue(state: AppState): number {
    const transaction = selectConfirmedTransaction(state);
    return transaction ? transaction.amount : NaN;
}

// Saagas
function* pollTransaction(id: string) {
    let jobSucceeded = false;
    while (!jobSucceeded) {
        const resp = yield call(transactionsApi.transactionLoad, id);
        if (resp.ok) {
            if (resp.data.status === 'finished') {
                jobSucceeded = true;
                return resp.data;
            } else if (resp.data.status === 'failed') {
                yield put(failedConfirmationPollingAction());
                jobSucceeded = true;
            }
        } else {
            yield put(failedConfirmationPollingAction());
        }
        yield call(delay, 1000);
    }
}

function* startPollingSaga({ payload }: any) {
    const { response, failed, timeout } = yield race({
        response: call(pollTransaction, payload),
        cancel: take(ConfirmationPollingActionTypes.Cancel),
        failed: take(ConfirmationPollingActionTypes.Failed),
        timeout: call(delay, 30000),
    });

    if (failed) {
        yield put(errorConfirmationPollingAction());
    }

    if (response) {
        const transaction: Transaction = response;

        sendGA4PurchaseEvent(transaction);
        sendTagManagerPurchase(
            transaction.uuid,
            transaction.entries.map(entry => ({
                item_id: entry.uuid,
                item_name: oc(entry).product.name.cz(''),
                item_variant: oc(entry).product.variant.cz(''),
                price: entry.amount,
                quantity: 1,
            }))
        );

        yield put(successConfirmationPollingAction(response));
        yield put(resetCartAction.request());
        yield put(resetTemporaryCardsAction());
        yield put(clearEmailStateAction());
    }

    if (timeout) {
        yield put(timeoutConfirmationPollingAction());
    }
}

export function* confirmationSaga() {
    yield takeLatest(ConfirmationPollingActionTypes.Start, startPollingSaga);
}

function getCodeTicketConfig(entry: Entry) {
    if (entry.meta.method === EntryMethod.Print) {
        return {
            type: 'pass',
            code: oc(entry).ticketSystem.output.printedData(''),
            serial: oc(entry).ticketSystem.output.permissionSerialNumber(''),
        };
    } else if (entry.type === 'card-product') {
        return { type: 'no-card-item', code: oc(entry).ticketSystem.output.printedData('') };
    } else if (entry.meta.method === EntryMethod.Prepaid || entry.meta.method === EntryMethod.Exchange) {
        return { type: 'exchange-pass', code: oc(entry).ticketSystem.output.prepaidData('') };
    } else {
        return { type: 'info' };
    }
}
